import { useState, useEffect, useRef, useCallback, useLayoutEffect, createContext, useContext, use, useMemo } from "react";
import { useIsDeveloper } from '@/systems/Authentication'
import { Icon } from '@/components/Icon'
import { NavLink, useNavigation, useActionData, useNavigate, useLocation, useRevalidator } from 'react-router'
import { Button, RoundIconButton } from '@/form/FormComponents'
import { useErrorModal, useHelpModal, useIsModalOpen } from '@/components/Modal';
import { useMenu } from '@/components/Menu';
import { useFormSubmit } from '@/form/FormProvider';
import { useResizeEffect, useIsMobile, useBreakpoint } from '@/hooks/useIsMobile';
import { useInmemoryStorage } from '@/hooks/useStorage';
import { useEvent } from "react-use-event-hook";

export function useIsFormSubmitting() {
  const navigation = useNavigation();

  let isRedirecting =
  navigation.state === "loading" &&
  (navigation.formData != null || navigation.json != null) &&
  navigation.formAction !== navigation.location.pathname;

  return isRedirecting || navigation.state === "submitting";
}

export function useIsReloading() {
  const navigation = useNavigation();
  const revalidator = useRevalidator();

  return (navigation.state === "loading" && navigation.location.pathname === window.location.pathname) || revalidator.state === "loading";
}

export function LoadingBar() {
  const navigation = useNavigation();
  const revalidator = useRevalidator();
  const [fresh, setFresh] = useState(true);
  const [loaderVisible, setLoaderVisible] = useState(false);
  const isDeveloper = useIsDeveloper();

  let loading = navigation.state === "loading" || navigation.state === "submitting" || revalidator.state === "loading";

  useEffect(function() {
    if (loading) {
      let still_loading = true;
      window.requestAnimationFrame(function() {
        window.requestAnimationFrame(function() {
          if (still_loading) {
            setLoaderVisible(true);
            setFresh(false);
          }
        })
      })
      return function() {
        still_loading = false;
      }
    } else {
      setFresh(true)
    }
  }, [loading])

  if (loading && fresh && loaderVisible) {
    setLoaderVisible(false);
  }

  let style;
  if (loading) {
    if (fresh) {
      style = {
        transition: "none",
        transform: "translateX(0%)"
      }
    } else {
      style = {
        transition: "",
        transform: "translateX(40%)"
      }
    }
  } else {
    style = {
      transform: ""
    }
  }

  if (!loaderVisible || (loading && fresh)) {
    style.visibility = "hidden";
  }

  return <>
    <div style={style} className={"loader" + (loading ? "" : " loader_complete")}></div>
    {isDeveloper ? <div className="developer_mode_indicator">Developer mode</div> : null}
  </>
}

const SidebarHeaderContext = createContext(null);

export function SidebarHeaderProvider({children}) {
  const [sidebar_open, setSidebarOpen] = useState(false);

  return (
    <SidebarHeaderContext.Provider value={{sidebar_open, setSidebarOpen}}>
      {children}
    </SidebarHeaderContext.Provider>
  )
}

/**
 * Given a target path, walks backward through the browser's navigation entries.
 * If the target is found and every entry in between is a sub-path of the target,
 * returns the negative delta (e.g. -5) to pass to navigate().
 * Otherwise returns null, meaning the caller should navigate forward to the target.
 */
export function getSmartBackDelta(targetPath) {
  if (!window.navigation || !window.navigation.entries || !window.navigation.currentEntry) return null

  const entries = window.navigation.entries()
  const currentIndex = window.navigation.currentEntry.index

  for (let i = currentIndex - 1; i >= 0; i--) {
    const pathname = new URL(entries[i].url).pathname
    if (pathname === targetPath) {
      return -(currentIndex - i)
    }
    if (!pathname.startsWith(targetPath + '/')) {
      return null
    }
  }

  return null
}

export function Header({title, breadcrumbs, backButton, menuItems: menuItemsProp, tabs, activeTab, setActiveTab, no_shadow = false, className, children}) {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarHeader = useContext(SidebarHeaderContext);
  const is_mobile = useIsMobile();
  const show_breadcrumbs = useBreakpoint((width) => width > 1100) && breadcrumbs;
  const [buttons_hidden, setButtonsHidden] = useState(0);
  const menuItems = (children ? (children.length > 0 ? children : [children]) : []).filter(child => child?.type?.name === "Button").map(child => ({
    label: child.props.text,
    onClick: child.props.onClick,
    icon: child.props.icon,
    loading: child.props.loading
  }));
  const visibleMenuItems = menuItems.slice(menuItems.length - buttons_hidden)
  const { handleOnClick } = useMenu({ menuItems: [...visibleMenuItems, ...((visibleMenuItems.length > 0 && menuItemsProp?.length > 0) ? ["separator"] : []), ...(menuItemsProp || [])] });
  const actionsRef = useRef(null);
  const titleRef = useRef(null);
  const moreRef = useRef(null);

  const is_rendered = !!(backButton || title || children);

  const recalculate = useCallback(() => {
    if (actionsRef.current) {
      const children = Array.from(actionsRef.current.children);

      children.forEach(child => {
        child.style.display = "";
      })
      moreRef.current.style.display = menuItemsProp?.length > 0 ? "" : "none";
      titleRef.current.style.flexGrow = "unset";

      let pend_buttons_hidden = 0;

      for (let i = 0; i < menuItems.length-1; i++) {
        
        const width_before = titleRef.current.clientWidth;

        children[menuItems.length - i - 1].style.display = "none";

        const width_after = titleRef.current.clientWidth;

        if (width_after > width_before) {
          pend_buttons_hidden++;
          moreRef.current.style.display = "";
        } else {
          children[menuItems.length - i - 1].style.display = "";
          break;
        }

      }

      titleRef.current.style.flexGrow = "";

      setButtonsHidden(pend_buttons_hidden);
    }
  }, [menuItems.length, menuItemsProp])

  useLayoutEffect(() => {
    recalculate()
  }, [recalculate])

  useResizeEffect(recalculate)

  useEffect(() => {
    if (is_rendered) {
      recalculate();
    }
  }, [is_rendered, recalculate]);
  
  const handleBackButton = useCallback(() => {
    if (backButton === true) {
      const canGoBack = location.key !== "default";
      navigate(canGoBack ? -1 : "/");
    } else {
      const delta = getSmartBackDelta(backButton);
      navigate(delta !== null ? delta : backButton);
    }
  }, [backButton, location.key, navigate]);
    
  return (
    <div className={"dashboard_header" + (no_shadow ? " no_shadow" : "")}>
      {is_rendered ? <div className={"dashboard_header_inner " + (className || "")}>
        {(sidebarHeader?.setSidebarOpen && is_mobile) ? <CircularMenuButton onClick={() => sidebarHeader?.setSidebarOpen(true)} icon="menu"/> : null}
        {(backButton && !show_breadcrumbs) ? <CircularMenuButton onClick={handleBackButton} icon="arrow-left"/> : null}
        <div className={"header_title" + ((show_breadcrumbs || typeof title !== "string") ? " header_title_component" : "")} ref={titleRef}>{show_breadcrumbs ? breadcrumbs : title}</div>
        <div className="header_actions buttons" ref={actionsRef}>
          {children}
        </div>
        <div className="header_menu" ref={moreRef}><RoundIconButton icon="dots-vertical" onClick={handleOnClick}/></div>
      </div> : null}
      <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab}/>
    </div>
  )
}

export function Tabs({ tabs, activeTab, setActiveTab, line = false }) {
  return (
    tabs && <div className={"dashboard_header_tabs" + (line ? " line" : "")}>
      {tabs.map((tab, index) => activeTab ? <div key={tab.id} className={"tab" + ((tab.id == activeTab) ? " active" : "")} tabIndex={0} onClick={() => {tab.onClick ? tab.onClick() : setActiveTab(tab.id)}}>{tab.icon && <Icon name={tab.icon}/>}<span>{tab.name}</span></div> : <NavLink key={index} to={tab.to} className="tab" end={tab.end}><span>{tab.name}</span></NavLink>)}
    </div>
  )
}

export const HeaderPillButton = ({ text, to, dropdown_icon = false, onClick, onContextMenu, disabled = false }) => {
  if (to) {
    return (
      <NavLink to={to} className={"header_title_pill_button"}>
        <span>{text}</span>
        {dropdown_icon && <Icon name="menu-down-enlarged"/>}
      </NavLink>
    )
  } else {
    return (
      <button onClick={onClick} onContextMenu={onContextMenu} className={"header_title_pill_button" + (disabled ? " disabled" : "")} disabled={disabled}>
        <span>{text}</span>
        {dropdown_icon && <Icon name="menu-down-enlarged"/>}
      </button>
    )
  }
}

export const HeaderRenamePillButton = ({ text, onClick, disabled = false }) => {
  return (
    <button onClick={onClick} className={"header_title_pill_button rename" + (disabled ? " disabled" : "")} disabled={disabled}>
      <span>{text}</span>
      <Icon name="pencil-outline"/>
    </button>
  )
}

export function InlineHeader({title, subtitle, small, children}) {
  return (
    <div className={"dashboard_inline_header" + (small ? " small" : "")}>
      <div className="inline_header_title">
        <div>{title}</div>
        {subtitle ? <div>{subtitle}</div> : null}
      </div>
      <div className="inline_header_actions buttons">
        {children}
      </div>
    </div>
  )
}

export function InlineSection({children, halfMarginBottom = false, halfMarginTop = false}) {
  return (
    <div className={"dashboard_inline_section" + (halfMarginBottom ? " half_margin_bottom" : "") + (halfMarginTop ? " half_margin_top" : "")}>
      {children}
    </div>
  )
}

export function Content({children}) {
  return (
    <div className="dashboard_content_inner nearest_scrollable_container">
      <div className="dashboard_content_container">
        {children}
      </div>
    </div>
  )
}

export function SidePanel({children}) {
  return (
    <div className="dashboard_side_panel">
      {children}
    </div>
  )
}

export function Sidebar({children, type="secondary", sidebar_open: sidebar_open_prop, setSidebarOpen: setSidebarOpen_prop}) {
  const sidebarHeader = useContext(SidebarHeaderContext);
  const sidebar_open = sidebarHeader?.sidebar_open || sidebar_open_prop || false;
  const setSidebarOpen = sidebarHeader?.setSidebarOpen || setSidebarOpen_prop || null;
  const [openHelpModal] = useHelpModal();
  const is_mobile = useIsMobile();
  const [was_open, setWasOpen] = useState(sidebar_open);
  const navigate = useNavigate();
  const navigation = useNavigation();
  const last_location = useRef(null);
  const location = useLocation();
  const is_modal_open = useIsModalOpen();
  const sidebarRef = useRef(null);
  const lastFocusedElementRef = useRef(null);

  // Set was_open to true after it's been opened once
  useEffect(() => {
    if (sidebar_open && !was_open) {
      setWasOpen(true);
    }
  }, [sidebar_open, was_open]);

  // Close sidebar when we're no longer mobile sized
  useEffect(() => {
    if (!is_mobile) {
      setWasOpen(false);
      setSidebarOpen?.(false);
    }
  }, [is_mobile, setSidebarOpen]);

  // Close sidebar when a modal opens
  useEffect(() => {
    if (is_modal_open) {
      setSidebarOpen?.(false);
    }
  }, [is_modal_open, setSidebarOpen]);

  // Close sidebar when navigation state changes to loading
  useEffect(() => {
    if (navigation.state === "loading") {
      setSidebarOpen?.(false);
    }
  }, [navigation, setSidebarOpen]);

  // Whenever we needed to load in order to navigate somewhere, store it so we don't trigger closing the sidebar when navigation finishes
  useEffect(() => {
    if (navigation.state === "loading" && navigation.location?.pathname) {
      last_location.current = navigation.location?.pathname;
    }
  }, [navigation]);

  // Close sidebar when a navigation finishes, but only if it didn't need to load
  useEffect(() => {
    if (location.pathname !== last_location.current) {
      setSidebarOpen?.(false);
    }
  }, [location, last_location, setSidebarOpen]);

  // Reset scroll position when the sidebar is opened
  useEffect(() => {
    if (sidebar_open) {
      const selected = sidebarRef.current.querySelector(".sidebar_button.selected");
      if (selected) {
        requestAnimationFrame(() => {
          selected.parentElement.scrollIntoViewIfNeeded();
        });
      } else {
        sidebarRef.current.scrollTop = 0;
      }
    }
  }, [sidebar_open]);

  // Focus management and focus trapping
  useEffect(() => {
    if (is_mobile && sidebar_open) {
      // Store the currently focused element
      lastFocusedElementRef.current = document.activeElement;
      
      // Focus the first focusable element in the sidebar
      const sidebar = sidebarRef.current;
      if (sidebar) {
        const focusableElements = sidebar.querySelectorAll(
          'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
        );
        const firstFocusable = focusableElements[0];
        firstFocusable?.focus();
      }
    } else if (is_mobile && !sidebar_open && lastFocusedElementRef.current) {
      // Return focus to the previously focused element when sidebar closes
      lastFocusedElementRef.current?.focus();
      lastFocusedElementRef.current = null;
    }
  }, [is_mobile, sidebar_open]);

  // Handle keyboard events for accessibility
  useEffect(() => {
    if (!is_mobile || !sidebar_open) return;

    const handleKeyDown = (event) => {
      // Close sidebar on Escape key
      if (event.key === 'Escape') {
        setSidebarOpen?.(false);
        return;
      }

      // Focus trapping - handle Tab key
      if (event.key === 'Tab') {
        const sidebar = sidebarRef.current;
        if (!sidebar) return;

        const focusableElements = sidebar.querySelectorAll(
          'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
        );
        
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab - if we're on the first focusable element, wrap to last
          if (document.activeElement === firstFocusable) {
            event.preventDefault();
            lastFocusable?.focus();
          }
        } else {
          // Tab - if we're on the last focusable element, wrap to first
          if (document.activeElement === lastFocusable) {
            event.preventDefault();
            firstFocusable?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [is_mobile, sidebar_open, setSidebarOpen]);

  return (
    <>
      {(is_mobile && (was_open || sidebar_open)) ? <div className={`sidebar_background modal_background ${(sidebar_open ? " modal_background_in" : " modal_background_out")}`} onClick={() => {setSidebarOpen?.(false)}} inert={!sidebar_open ? "true" : undefined}></div> : null}
      <nav className={"dashboard_sidebar" + (is_mobile ? (" mobile" + (sidebar_open ? " sidebarIn" : (was_open ? " sidebarOut" : ""))) : "") + (type === "secondary" ? " secondary" : "")} inert={(is_mobile && !sidebar_open) ? "true" : undefined} ref={sidebarRef}>
        {is_mobile && <div className="dashboard_sidebar_top_mobile">
          <CircularMenuButton onClick={() => {setSidebarOpen?.(false)}} icon="menu"/>
          {type === "main" && (
            <a className="logo" onClick={() => navigate("/")}>
              <Icon name="Lockup"/>
            </a>
          )}
        </div>}
        <section>
          <ul>
            {children}
          </ul>
        </section>
        {type === "main" && (
          <div className="dashboard_sidebar_footer">
            <div>© 2026 Sapient Clinician LLC</div>
            <div><a onClick={openHelpModal} tabIndex={0}>Help</a><a href="https://sapientclinician.com/privacy" target="_blank">Privacy</a><a href="https://sapientclinician.com/terms" target="_blank">Terms</a></div>
          </div>
        )}
      </nav>
    </>
  )
}

export function SidebarButton({ to, icon, name, selected, onClick, indent, big, outlined, disabled, shimmer, show_dot = false, end, replace }) {
  const sidebarHeader = useContext(SidebarHeaderContext);
  const handleClick = (e) => {
    sidebarHeader?.setSidebarOpen?.(false);
    onClick?.(e);
  }
  // shimmer = false;
  return (
    <li>
      {to ?
        <NavLink to={to} end={end} replace={replace} className={({isActive}) => "sidebar_button" + ((selected != undefined && selected !== "default") ? (selected ? " selected" : "") : (isActive ? " selected" : "")) + (indent ? " indent-"+indent : "") + (big ? " big" : "") + (outlined ? " outlined" : "")}>
          {icon ? <Icon name={icon}/> : null}
          <span>{name}</span>
          {show_dot ? <Icon name="dot"/> : null}
        </NavLink>
      :
        <a className={"sidebar_button" + (selected ? " selected" : "") + (indent ? " indent-"+indent : "") + (big ? " big" : "") + (outlined ? " outlined" : "") + (disabled ? " disabled" : "") + (shimmer ? " ai_shimmer ai_shimmer_"+shimmer : "")} tabIndex={0} inert={disabled ? "true" : undefined} onClick={handleClick}>
          {icon ? <Icon name={icon}/> : null}
          <span>{name}</span>
          {show_dot ? <Icon name="dot"/> : null}
        </a>
      }
    </li>
  )
}

export function SidebarSpacing() {
  return (
    <li className="sidebar_spacing"></li>
  )
}

export function Actionbar({title, children}) {
  return (
    <div className="dashboard_actionbar">
      <div className="dashboard_actionbar_inner">
        {children}
      </div>
    </div>
  )
}

export function ActionbarLeft({children}) {
  return (
    <div className="actionbar_left">
      {children}
    </div>
  )
}

export function ActionbarRight({children}) {
  return (
    <div className="actionbar_right">
      {children}
    </div>
  )
}

export function CircularMenuButton({icon, disabled, onClick}) {
  return (
    <a className={"circular_menu_button "+(disabled ? "disabled" : "")} tabIndex={disabled ? -1 : 0} onClick={disabled ? () => {} : onClick}>
      <Icon name={icon}/>
    </a>
  )
}

const ScrollableContext = createContext(null);

export const Scrollable = ({ children, location_key: location_key_prop, depencencies = [], remember = false, style = {}, ...props }) => {
  const location = useLocation();
  const location_key = location_key_prop || location.key;

  const [,updateScrollRestoration] = useInmemoryStorage("scroll_restoration", []);

  const scroller_ref = useRef();
  const [topShadowAmont, setTopShadowAmount] = useState(0);
  const [bottomShadowAmont, setBottomShadowAmount] = useState(0);
  const dashboardContentStyles = {
    "--top_shadow_amount": topShadowAmont,
    "--bottom_shadow_amount": bottomShadowAmont
  }

  useEffect(() => {
    if (remember) {
      const new_scroll_restoration = updateScrollRestoration(draft => {
        if (!draft.find(entry => entry.key == location_key)) {
          draft.push({ key: location_key, scroll_position: 0 });
        }
        return draft;
      })

      const scroll_position = new_scroll_restoration.find(entry => entry.key == location_key).scroll_position;

      let scroller = scroller_ref.current;
      if (scroller && !scroller.classList.contains(".nearest_scrollable_container")) {
        scroller = scroller.querySelector(".nearest_scrollable_container:not([hidden])");
      }
      if (scroller) {
        scroller.scrollTop = scroll_position;
      }
    }
  }, [location_key, updateScrollRestoration, remember])

  const rememberScrollPosition = useCallback((pos) => {
    if (remember) {
      updateScrollRestoration(draft => {
        draft.find(entry => entry.key == location_key).scroll_position = pos;
        return draft;
      })
    }
  }, [location_key, updateScrollRestoration, remember])
  
  useEffect(function() {
    let scroller = scroller_ref.current;
    if (scroller && !scroller.classList.contains(".nearest_scrollable_container")) {
      scroller = scroller.querySelector(".nearest_scrollable_container:not([hidden])");
    }
    if (scroller) {
      let scroller_inner = scroller.classList.contains("modal_content") ? scroller : scroller.querySelector(".dashboard_content_container");
      const handleScrollEvent = () => {
        let scroller_shadow_offset_height = Array.from(scroller.querySelectorAll(".scroller_shadow_offset_height")).map(element => element.offsetHeight).reduce((a, b) => a + b, 0);
        setTopShadowAmount(Math.min(1, (scroller.scrollTop-scroller_shadow_offset_height)/14).toFixed(2));
        setBottomShadowAmount(Math.min(1, (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight)/14).toFixed(2));
        if (scroller.scrollHeight > scroller.clientHeight) {
          rememberScrollPosition(scroller.scrollTop);
        }
      }
      scroller.addEventListener('scroll', handleScrollEvent);
      window.addEventListener('resize', handleScrollEvent);
      let content_resize_observer;
      let content_mutation_observer;
      if (scroller_inner) {
        content_resize_observer = new ResizeObserver(handleScrollEvent);
        content_resize_observer.observe(scroller_inner);
        content_mutation_observer = new MutationObserver(handleScrollEvent);
        content_mutation_observer.observe(scroller_inner, { 
          childList: true, 
          subtree: true, 
          attributes: true,
          characterData: true
        });
      }
      handleScrollEvent();
      return function() {
        scroller.removeEventListener('scroll', handleScrollEvent);
        window.removeEventListener('resize', handleScrollEvent);
        if (content_resize_observer) {
          content_resize_observer.disconnect();
        }
        if (content_mutation_observer) {
          content_mutation_observer.disconnect();
        }
      }
    } else {
      setTopShadowAmount(0);
      setBottomShadowAmount(0);
    }
  }, [location.pathname, location_key, ...depencencies, rememberScrollPosition]);

  const scrollToTop = useEvent(() => {
    let scroller = scroller_ref.current;
    if (scroller && !scroller.classList.contains(".nearest_scrollable_container")) {
      scroller = scroller.querySelector(".nearest_scrollable_container:not([hidden])");
    }
    if (scroller) {
      scroller.scrollTop = 0;
    }
  }, [scroller_ref]);

  const ctx = useMemo(() => ({ scrollToTop, scroller_ref }), [scrollToTop, scroller_ref]);

  return (
    <div {...props} ref={scroller_ref} style={{ ...style, ...dashboardContentStyles }}>
      <ScrollableContext.Provider value={ctx}>
        {children}
      </ScrollableContext.Provider>
    </div>
  )
}

export const useScrollable = () => {
  return use(ScrollableContext) || {};
}

export function Pagination({show_back, show_continue, children, getContainerElement, autosave_indicator}) {
  function handlePrevious() {
    getContainerElement().querySelector(".sidebar_button.selected").parentElement.previousElementSibling.children[0].click()
  }

  function handleNext() {
    const child1 = getContainerElement().querySelector(".sidebar_button.selected").parentElement.nextElementSibling?.children?.[0];
    const child2 = getContainerElement().querySelector(".sidebar_button.selected").parentElement.nextElementSibling?.nextElementSibling?.children?.[0];
    if (child1) {
      child1.click();
    } else if (child2) {
      child2.click();
    }
  }

  return (
    <Actionbar>
      <ActionbarLeft>
        {null /* {show_back ? <Button text="Back" icon="chevron-left" onClick={handlePrevious}/> : null} */}
        {autosave_indicator ? <AutosaveIndicator/> : null}
      </ActionbarLeft>
      <ActionbarRight>
        {show_continue ? <Button text="Continue" className="primary" icon="chevron-right" icon_right={true} onClick={handleNext}/> : children}
      </ActionbarRight>
    </Actionbar>
  )
}

export function AutosaveIndicator() {
  const { autosave_status, has_changed } = useFormSubmit();

  return (
    <div className="autosave_indicator">
      {
        (autosave_status == "saving" || (has_changed && autosave_status !== "error")) ? <>
          <Icon name="sync-custom"/>
          <span>Saving...</span>
        </> : (
          autosave_status == "saved" ? <>
            <Icon name="cloud-check-variant-outline"/>
            <span>Saved</span>
          </> : (
            autosave_status == "error" ? <>
              <Icon name="sync-alert"/>
              <span>Error saving</span>
            </> : null
          )
        )
      }
    </div>
  )
}

export function useHandleSubmitError() {
  const failed = useActionData()?.success === false;
  const failed_date = useActionData()?.date;
  
  const [openErrorModal] = useErrorModal();

  useEffect(function() {
    if (failed) {
      openErrorModal();
    }
  }, [failed, failed_date])
}
